/**
 * VendorSessionContext — Instance unique de la session vendeur
 *
 * Centralise la logique de session vendeur (ping, auto-save, switch)
 * dans un seul Provider pour éviter les instances multiples
 * (VendorQuickBar + POSWorkspace avaient chacun leur propre hook).
 *
 * Fixes appliqués vs l'ancien useVendorSession :
 * - Instance unique via Context (plus de double ping / double state)
 * - Auto-save utilise usePOSStore.getState() au lieu de closures stales
 * - Échec de sauvegarde avant switch → toast d'avertissement (plus silencieux)
 * - switchVendor deps complètes
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { usePOSStore, useVendorCart } from '../hooks/usePOSStore';
import type { Vendor, VendorSession, VendorRole } from '../types/pos.types';
import { CatalogSync } from '../lib/offline/CatalogSync';
import { syncProduceIndex, setProduceSource, loadFlCategorySet } from '../services/camera/produceIndex';
import { useTenant } from './TenantContext';
import { offlineDb } from '../lib/offline/OfflineDatabase';
import { logger } from '../utils/logger';

interface VendorSessionState {
  currentVendor: Vendor | null;
  session: VendorSession | null;
  hasLock: boolean;
  isOnline: boolean;
  loading: boolean;
  error: string | null;
}

interface VendorSessionContextValue extends VendorSessionState {
  switchVendor: (vendorId: string, pin?: string, takeover?: boolean, onLocked?: (workstation?: string) => void) => Promise<boolean>;
  loginOfflineVendor: (vendorId: string, vendorName: string, vendorRole?: VendorRole) => void;
  logout: () => Promise<void>;
  saveCart: () => Promise<void>;
  restoreCart: (vendorId: string) => Promise<void>;
}

const VendorSessionCtx = createContext<VendorSessionContextValue | null>(null);

// Générer un ID unique pour le workstation (iPad)
// Stocké dans localStorage pour persister entre rechargements
function getWorkstationId(): string {
  const key = 'folies_workstation_id';

  // Réinitialisation à distance : ouvrir /pos?resetws=1 régénère l'identifiant du
  // poste. Nécessaire quand plusieurs caisses partagent le MÊME id (configuration
  // recopiée d'une machine à l'autre lors de l'installation) : le serveur ne peut
  // plus les distinguer, le verrou de session ne protège plus rien et deux postes
  // peuvent écrire le même panier (incident du 31/07/2026 — vendeuse connectée sur
  // deux caisses sans alerte, panier ressuscité après encaissement).
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('resetws')) {
      const fresh = `pos-${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem(key, fresh);
      params.delete('resetws');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      logger.warn(`[VendorSession] Identifiant de poste régénéré : ${fresh}`);
      return fresh;
    }
  } catch {
    // Pas de blocage si l'URL est illisible
  }

  let id = localStorage.getItem(key);

  if (!id) {
    id = `pos-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(key, id);
  }

  return id;
}

interface CartPayload {
  lines: any[];
  customerId: string | null;
  preorderId: string | null;
  activeTareGrams: number;
  invoiceRequested?: boolean;
  // Le LIEN à la commande eshop en cours de remise. Sans lui, un changement de poste
  // (ou une reprise de session) rendait les lignes du panier mais PAS le fait qu'elles
  // étaient déjà payées en ligne : le vendeur réclamait alors la totalité au client.
  ecomOrderId?: string | null;
  ecomOrderPaid?: boolean;
  ecomOrderDueCents?: number;
  ecomOrderPaidCents?: number;
}

/**
 * Sauvegarde du panier avec retry court — réseau marché instable (incident 07/06/2026,
 * 4 vendeurs / 3 POS). Une coupure d'une seconde ne doit pas faire perdre le panier d'un
 * vendeur qui change de poste. Renvoie true si le serveur a confirmé, false sinon.
 * Ne jette jamais. Un 409 (lock pris par un autre poste) n'est pas réessayé.
 */
// Résultat distinct : on doit savoir POURQUOI une sauvegarde a échoué.
//  - 'ok'          : persisté côté serveur ;
//  - 'conflict'    : 409 — ce poste ne détient PAS le lock (vendeur repris ailleurs /
//                    session expirée). Le panier autoritatif vit ailleurs → NE PAS alarmer
//                    l'opérateur ni garder un filet local (qui écraserait plus frais) ;
//  - 'unreachable' : serveur injoignable après retries (réseau marché) → là, le panier
//                    n'est QUE local → on prévient l'opérateur avant de quitter le vendeur.
type SaveCartResult = 'ok' | 'conflict' | 'unreachable';

async function saveVendorCartResilient(
  vendorId: string,
  payload: CartPayload,
  workstationId: string,
): Promise<SaveCartResult> {
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await api.saveVendorCart(vendorId, payload, workstationId);
      // Panier vide confirmé côté serveur : purger le filet local, sinon il
      // ressusciterait un panier déjà traité à la prochaine ouverture.
      const vide = payload.lines.length === 0 && !(payload.activeTareGrams > 0)
        && !payload.customerId && !payload.invoiceRequested;
      if (vide) {
        try { localStorage.removeItem(`vendor_cart_${vendorId}`); } catch { /* noop */ }
      }
      return 'ok';
    } catch (err: any) {
      if (err?.response?.status === 409) {
        logger.warn('[VendorSession] saveCart 409 (lock ailleurs) — pas de retry, pas d\'alerte');
        return 'conflict';
      }
      logger.warn(`[VendorSession] saveCart échec ${attempt}/3:`, err);
      if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 400));
    }
  }
  return 'unreachable';
}

/**
 * Filet « panier non synchronisé ». La clé localStorage vendor_cart_<id> n'existe QUE
 * lorsqu'une sauvegarde serveur a échoué (réseau) ou lors d'un logout offline — elle
 * représente donc un panier PLUS RÉCENT que le snapshot serveur. On l'utilise pour
 * restaurer quand le serveur ne renvoie rien d'exploitable. Garde de fraîcheur 6 h pour
 * éviter de ressusciter un vieux panier resté coincé sur un poste hors-ligne.
 */
/** Empreinte du panier courant (lignes, client, précommande, commande en ligne). */
function empreintePanier(s: any): string {
  const v = s?.vendorId;
  return JSON.stringify({
    l: v ? s.carts?.[v] || [] : [],
    c: s?.customerId ?? null,
    p: s?.preorderId ?? null,
    e: s?.ecomOrderId ?? null,
  });
}

export function readPendingCart(vendorId: string): CartPayload | null {
  try {
    const raw = localStorage.getItem(`vendor_cart_${vendorId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const fresh = typeof parsed.timestamp === 'number' && Date.now() - parsed.timestamp < 6 * 60 * 60 * 1000;
    const hasContent = (parsed.lines && parsed.lines.length > 0)
      || (parsed.activeTareGrams ?? 0) > 0
      || !!parsed.customerId
      || !!parsed.invoiceRequested;
    if (!fresh || !hasContent) return null;
    return {
      lines: parsed.lines || [],
      customerId: parsed.customerId ?? null,
      preorderId: parsed.preorderId ?? null,
      activeTareGrams: parsed.activeTareGrams ?? 0,
      invoiceRequested: !!parsed.invoiceRequested,
      ecomOrderId: parsed.ecomOrderId ?? null,
      ecomOrderPaid: !!parsed.ecomOrderPaid,
      ecomOrderDueCents: parsed.ecomOrderDueCents ?? 0,
      ecomOrderPaidCents: parsed.ecomOrderPaidCents ?? 0,
    };
  } catch (err) {
    logger.error('[VendorSession] readPendingCart parse échoué:', err);
    return null;
  }
}

/**
 * Écrit le filet « panier non synchronisé » de ce vendeur, ou l'efface si le
 * panier est vide. Appelé chaque fois qu'une modification du panier n'a PAS pu
 * être confirmée par le serveur (pas de session, sauvegarde en échec).
 *
 * Incident du 10/10/2026 (POS-01) : la caisse est restée 10 minutes sans
 * session ; le panier d'Agathe (13 lignes) n'existait que dans la mémoire du
 * poste, et le remontage de l'écran l'a pris pour un reste périmé et effacé.
 * Avec ce filet, le remontage le retrouve (`readPendingCart`) au lieu de le jeter.
 */
export function writePendingCart(vendorId: string, payload: CartPayload): void {
  const vide = payload.lines.length === 0 && !(payload.activeTareGrams > 0)
    && !payload.customerId && !payload.invoiceRequested;
  try {
    if (vide) localStorage.removeItem(`vendor_cart_${vendorId}`);
    else localStorage.setItem(`vendor_cart_${vendorId}`, JSON.stringify({ vendorId, ...payload, timestamp: Date.now() }));
  } catch (e) {
    logger.warn('[VendorSession] Filet local panier échoué:', e);
  }
}

/**
 * Le serveur a RÉPONDU que ce vendeur n'existe pas ou n'est plus actif (404/403).
 * À ne surtout pas confondre avec une coupure réseau : le 10/10/2026, Paul a été
 * retiré du tableau de service pendant qu'il était sur POS-01 ; la reprise de sa
 * session a reçu un 404, traité comme « hors ligne », et la caisse a tourné
 * sans session — donc sans aucune sauvegarde — pendant 10 minutes.
 */
export function vendeurRefuse(err: any): boolean {
  const statut = err?.response?.status;
  return statut === 404 || statut === 403;
}

/** Lire les valeurs actuelles du store Zustand (jamais stale) */
function getPOSStoreSnapshot() {
  const s = usePOSStore.getState();
  return {
    vendorId: s.vendorId,
    customerId: s.customerId,
    preorderId: s.preorderId,
    invoiceRequested: s.invoiceRequested,
    ecomOrderId: s.ecomOrderId,
    ecomOrderPaid: s.ecomOrderPaid,
    ecomOrderDueCents: s.ecomOrderDueCents,
    ecomOrderPaidCents: s.ecomOrderPaidCents,
    setEcomOrder: s.setEcomOrder,
    currentVendorName: s.currentVendorName,
    currentVendorRole: s.currentVendorRole,
    activeTareGrams: s.activeTareGrams,
    paymentInProgress: s.paymentInProgress,
    getCurrentVendorCart: s.getCurrentVendorCart,
    clearCart: s.clearCart,
    setVendorId: s.setVendorId,
    setCurrentVendorName: s.setCurrentVendorName,
    setCurrentVendorRole: s.setCurrentVendorRole,
    setVendorWorkstations: s.setVendorWorkstations,
    loadCartFromSnapshot: s.loadCartFromSnapshot,
    setFullAccess: s.setFullAccess,
    resetFullAccess: s.resetFullAccess,
  };
}

export function VendorSessionProvider({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  const [state, setState] = useState<VendorSessionState>({
    currentVendor: null,
    session: null,
    hasLock: false,
    isOnline: navigator.onLine,
    loading: false,
    error: null,
  });

  const workstationId = useRef(getWorkstationId());
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lockLostCountRef = useRef(0);
  const pingFailCountRef = useRef(0); // Échecs de ping consécutifs avant de déclarer offline
  const lastSavedCartHashRef = useRef<string>('');
  // #2 : verrou anti-course de switch. Posé à true pendant toute la transition d'un
  // changement de vendeur (online ET offline) ; aucun auto-save ne s'exécute tant qu'il
  // est true. Évite la fenêtre où store.vendorId a basculé mais state.session pas encore.
  const switchInProgress = useRef(false);
  // Miroir non-stale de state.session, lisible dans les handlers à deps vides (online/offline)
  const sessionRef = useRef<VendorSession | null>(null);
  useEffect(() => { sessionRef.current = state.session; }, [state.session]);
  const hasLockRef = useRef(false);
  useEffect(() => { hasLockRef.current = state.hasLock; }, [state.hasLock]);
  // Reprise de session « en ligne mais sans session » (voir la réconciliation plus bas).
  const reprendreSessionRef = useRef<(() => Promise<void>) | null>(null);
  /**
   * Empreinte du panier RÉHYDRATÉ lors d'un montage hors ligne. Tant que
   * l'opérateur n'y a pas touché, ce n'est pas un panier « en cours » mais
   * peut-être un reste déjà vendu ailleurs : le recopier dans le filet avec
   * un horodatage frais le ferait ressusciter à l'ouverture suivante.
   */
  const empreinteMontageRef = useRef<string | null>(null);
  const derniereRepriseRef = useRef(Date.now());

  /**
   * Le vendeur sélectionné sur ce poste n'est plus actif : on le désélectionne et
   * on demande d'en choisir un, au lieu de faire semblant d'être hors ligne. Son
   * panier éventuel reste dans le filet local (il revient si le vendeur est réactivé).
   */
  const oublierVendeurInactif = useCallback((vendorId: string, nom: string | null) => {
    const live = usePOSStore.getState();
    const lignes = live.carts[vendorId] || [];
    writePendingCart(vendorId, {
      lines: lignes,
      customerId: live.customerId,
      preorderId: live.preorderId,
      activeTareGrams: live.tares[vendorId] ?? live.activeTareGrams,
      invoiceRequested: live.invoiceRequested,
      // Le lien à la commande en ligne PAYÉE voyage avec le panier : sans lui,
      // au retour du vendeur, la caisse réclamait la totalité au client.
      ecomOrderId: live.ecomOrderId,
      ecomOrderPaid: live.ecomOrderPaid,
      ecomOrderDueCents: live.ecomOrderDueCents,
      ecomOrderPaidCents: live.ecomOrderPaidCents,
    });
    const store = getPOSStoreSnapshot();
    store.clearCart();
    store.setVendorId(null);
    store.setCurrentVendorName(null);
    store.setCurrentVendorRole(null);
    store.setVendorWorkstations([]);
    store.resetFullAccess();
    setState((prev) => ({
      ...prev, currentVendor: null, session: null, hasLock: false, isOnline: true, loading: false, error: null,
    }));
    toast.error(
      `${nom || 'Ce vendeur'} n'est plus actif sur la caisse — choisissez un vendeur`
        + (lignes.length > 0 ? ` (son panier de ${lignes.length} ligne${lignes.length > 1 ? 's' : ''} est conservé)` : ''),
      { duration: 10000 },
    );
    logger.warn(`[VendorSession] Vendeur ${vendorId} inactif ou inconnu — désélectionné`);
  }, []);

  // ─── Online / Offline ────────────────────────────────────────
  useEffect(() => {
    // Reprendre le lock serveur et repousser le panier courant. Sert au retour du
    // réseau (événement `online`) ET à la réconciliation périodique : un poste
    // peut être en ligne sans session sans que le navigateur émette jamais
    // `online` (changement de vendeur fait « hors ligne » sur un faux négatif).
    const reprendreSession = async () => {
      const store = getPOSStoreSnapshot();
      const vendorId = store.vendorId;
      if (!vendorId) return;

      // 1. Ré-acquérir le lock serveur si on n'en a pas (ex: switch effectué hors-ligne → session=null).
      //    #1 : SANS forcer. Si la session est vivante sur un autre poste → 409 → on reste
      //    « Ailleurs » (pas de vol) et on NE resynchronise PAS (on ne détient pas le lock).
      //    Si lock périmé/inexistant → le backend réattribue automatiquement.
      let canResync = true;
      if (!sessionRef.current) {
        try {
          const resp = await api.acquireVendorSession(vendorId, workstationId.current, false, false);
          if (resp?.access_token) localStorage.setItem('auth_token', resp.access_token);
          lockLostCountRef.current = 0;
          setState((prev) => ({ ...prev, session: resp, hasLock: true, error: null }));
          logger.log('[VendorSession] 🔐 Session ré-acquise au retour réseau');
        } catch (e: any) {
          const code = e?.response?.data?.code;
          if (vendeurRefuse(e)) {
            oublierVendeurInactif(vendorId, store.currentVendorName);
            return;
          }
          if (code === 'SESSION_LOCKED' || code === 'PAYMENT_IN_PROGRESS') {
            logger.warn('[VendorSession] Retour réseau : vendeur actif/encaisse sur un autre poste — pas de vol');
            setState((prev) => ({ ...prev, hasLock: false, error: 'Session active sur un autre poste' }));
            canResync = false; // on ne détient pas le lock → ne pas pousser le panier
          } else {
            logger.warn('[VendorSession] Ré-acquisition session au retour réseau échouée:', e);
          }
        }
      }

      // 2. Resynchroniser le panier COURANT (modifs hors-ligne) vers la DB, seulement si on
      //    peut détenir le lock. On ne pousse jamais un panier vide. saveVendorCartResilient
      //    passe le workstation_id → le backend rejette (409) si le lock est ailleurs.
      const currentCart = store.getCurrentVendorCart();
      if (canResync && (currentCart.length > 0 || store.activeTareGrams > 0)) {
        const result = await saveVendorCartResilient(vendorId, {
          lines: currentCart,
          customerId: store.customerId,
          preorderId: store.preorderId,
          activeTareGrams: store.activeTareGrams,
          invoiceRequested: store.invoiceRequested,
          ecomOrderId: store.ecomOrderId,
          ecomOrderPaid: store.ecomOrderPaid,
          ecomOrderDueCents: store.ecomOrderDueCents,
          ecomOrderPaidCents: store.ecomOrderPaidCents,
        }, workstationId.current);
        if (result === 'ok') {
          lastSavedCartHashRef.current = '';
          try { localStorage.removeItem(`vendor_cart_${vendorId}`); } catch { /* noop */ }
          logger.log('[VendorSession] Panier courant resynchronisé au retour réseau');
        }
      } else if (currentCart.length === 0 && store.activeTareGrams === 0) {
        // Panier vide : nettoyer un éventuel filet local obsolète
        try { localStorage.removeItem(`vendor_cart_${vendorId}`); } catch { /* noop */ }
      }
    };
    reprendreSessionRef.current = reprendreSession;

    const handleOnline = async () => {
      setState((prev) => ({ ...prev, isOnline: true }));
      logger.log('🟢 Application online');
      derniereRepriseRef.current = Date.now();
      await reprendreSession();
    };

    const handleOffline = () => {
      setState((prev) => ({ ...prev, isOnline: false }));
      logger.log('🔴 Application offline - verrouillage sur ce poste');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ─── Réconciliation isOnline sans session ────────────────────
  // Quand AUCUNE session n'est active (pas de vendeur → pas de heartbeat qui gère isOnline),
  // on réaligne isOnline sur navigator.onLine périodiquement. Évite qu'un événement online/
  // offline manqué (Safari/macOS au boot ou réveil WiFi) laisse isOnline figé à « faux ».
  // Avec une session active, c'est le ping qui pilote isOnline → cet effet se désactive.
  useEffect(() => {
    if (state.session) return;
    const sync = () => {
      setState((prev) => (prev.isOnline !== navigator.onLine ? { ...prev, isOnline: navigator.onLine } : prev));
      // Un vendeur est sélectionné, ce poste « détient » son panier (pas « Ailleurs »),
      // le réseau est là… mais aucune session : ni ping, ni sauvegarde. Rien ne
      // l'en sortait (10/10/2026, POS-01, 10 minutes). On reprend la session,
      // au plus une tentative toutes les 15 s.
      const store = getPOSStoreSnapshot();
      if (
        navigator.onLine && store.vendorId && hasLockRef.current && !switchInProgress.current
        && Date.now() - derniereRepriseRef.current > 15_000
      ) {
        derniereRepriseRef.current = Date.now();
        logger.log('[VendorSession] En ligne sans session — reprise de la session');
        void reprendreSessionRef.current?.();
      }
    };
    sync();
    const id = setInterval(sync, 5000);
    return () => clearInterval(id);
  }, [state.session]);

  // ─── Stockage persistant ─────────────────────────────────────
  // Demande à WebKit/Safari de ne PAS évincer IndexedDB (catalogue offline, ventes en
  // file). Sans ça, la politique ITP purge le stockage après ~7 jours sans usage du site
  // → un Mac mini POS resté éteint une semaine perdrait son catalogue synchronisé.
  useEffect(() => {
    (async () => {
      try {
        if (navigator.storage?.persist) {
          const already = await navigator.storage.persisted?.();
          if (!already) {
            const granted = await navigator.storage.persist();
            logger.log(`[VendorSession] Stockage persistant ${granted ? 'accordé ✅' : 'refusé ⚠️'}`);
          }
        }
      } catch (e) {
        logger.warn('[VendorSession] storage.persist() indisponible:', e);
      }
    })();
  }, []);

  // ─── Re-acquire session au montage (retour sur le POS après navigation) ───
  useEffect(() => {
    const store = getPOSStoreSnapshot();
    if (store.vendorId && !state.session) {
      // Un vendeur était sélectionné mais pas de session active → re-acquire
      logger.log(`[VendorSession] Re-acquire automatique pour ${store.currentVendorName || store.vendorId} au montage du Provider`);
      (async () => {
        try {
          // #1 : NE PAS forcer au montage. Si la session est vivante sur un autre poste,
          // on tombe sur un 409 (géré dans le catch) → « Ailleurs », pas de vol. Si le lock
          // est périmé (poste précédent mort >5 min) → le backend réattribue automatiquement.
          const sessionResponse = await api.acquireVendorSession(
            store.vendorId!,
            workstationId.current,
            false, // force
            false, // takeover
          );

          if (sessionResponse.access_token) {
            localStorage.setItem('auth_token', sessionResponse.access_token);
          }

          const vendor = await api.getVendor(store.vendorId!);

          // #3 : la réponse d'acquireVendorSession est PLATE (vendor_id, workstation_id…
          // au top niveau). L'ancien `sessionResponse.session` valait undefined → state.session
          // restait null → ping/auto-save jamais démarrés après un retour sur le POS.
          setState((prev) => ({
            ...prev,
            currentVendor: vendor,
            session: sessionResponse,
            hasLock: sessionResponse.workstation_id === workstationId.current,
            loading: false,
          }));

          // Charger le panier distant (+ tare, client, demande de facture persistés).
          // Le snapshot serveur est la source de vérité. S'il ne renvoie rien d'exploitable
          // (snapshot vide après une sauvegarde échouée pendant une coupure), on bascule sur
          // le filet local « non synchronisé » plutôt que d'écraser le panier en cours.
          let remoteSnapshot = sessionResponse.cart_snapshot;
          const serverHasCart = !!(
            remoteSnapshot && ((remoteSnapshot.lines?.length ?? 0) > 0 || (remoteSnapshot.activeTareGrams ?? 0) > 0
              || remoteSnapshot.customerId || remoteSnapshot.invoiceRequested)
          );
          if (!serverHasCart) {
            const pending = readPendingCart(store.vendorId!);
            if (pending) {
              remoteSnapshot = pending;
              // Consommé une seule fois : sans cette suppression, un panier déjà
              // traité ailleurs se re-horodatait à chaque fermeture hors-ligne de
              // l'iPad et ressuscitait à CHAQUE réouverture (29-30/08/2026).
              try { localStorage.removeItem(`vendor_cart_${store.vendorId}`); } catch { /* noop */ }
              logger.log(`[VendorSession] Re-acquire montage : panier restauré depuis le filet local (snapshot serveur vide)`);
            }
          }
          const restoreHasContent = !!(
            remoteSnapshot && ((remoteSnapshot.lines?.length ?? 0) > 0 || (remoteSnapshot.activeTareGrams ?? 0) > 0
              || remoteSnapshot.customerId || remoteSnapshot.invoiceRequested)
          );
          if (restoreHasContent) {
            const remoteTare = typeof remoteSnapshot.activeTareGrams === 'number'
              ? remoteSnapshot.activeTareGrams
              : 0;
            usePOSStore.setState((s) => ({
              carts: { ...s.carts, [store.vendorId!]: remoteSnapshot.lines || [] },
              tares: { ...s.tares, [store.vendorId!]: remoteTare },
              activeTareGrams: remoteTare,
              customerId: remoteSnapshot.customerId || null,
              preorderId: remoteSnapshot.preorderId || null,
              invoiceRequested: !!remoteSnapshot.invoiceRequested,
              ecomOrderId: remoteSnapshot.ecomOrderId || null,
              ecomOrderPaid: !!remoteSnapshot.ecomOrderPaid,
              ecomOrderDueCents: remoteSnapshot.ecomOrderDueCents ?? 0,
              ecomOrderPaidCents: remoteSnapshot.ecomOrderPaidCents ?? 0,
            }));
          } else {
            // Le serveur a RÉPONDU et ne porte aucun panier, et le filet local n'a rien
            // d'exploitable (absent, vide, ou vieux de plus de 6 h) : la copie persistée
            // de CE poste est périmée. On l'efface — c'est déjà ce que fait le changement
            // de vendeur (« Aucun panier sauvegardé → état vide »), et le montage était le
            // seul chemin à ne pas le faire.
            //
            // Sans ça, le panier du store (persisté sous `folies-pos-store`, réhydraté
            // AVANT cet effet) restait affiché à CHAQUE ouverture : c'est la deuxième
            // copie, celle que le correctif du 30/08 sur `vendor_cart_*` ne touchait pas.
            // Passé 6 h, le filet cessait bien de restaurer — mais le panier s'affichait
            // quand même, et la garde de fraîcheur ne protégeait de rien (iPad, riz à
            // l'indienne, 03/09/2026).
            //
            // Sûr par construction : cet effet ne tourne qu'au MONTAGE, avant toute saisie
            // possible de l'opérateur — le panier présent ici ne peut venir que de la
            // persistance. Et hors-ligne, `acquireVendorSession` échoue : on part dans le
            // `catch`, qui conserve la copie locale (cas marché).
            const avait = (usePOSStore.getState().carts[store.vendorId!] || []).length;
            if (avait > 0 || usePOSStore.getState().activeTareGrams > 0) {
              logger.log(`[VendorSession] Montage : snapshot serveur vide → panier local périmé effacé (${avait} ligne(s))`);
            }
            store.clearCart();
            usePOSStore.setState((s) => ({
              tares: { ...s.tares, [store.vendorId!]: 0 },
              activeTareGrams: 0,
            }));
            // Le filet est périmé lui aussi : le laisser en place le ferait relire à
            // chaque ouverture jusqu'à expiration silencieuse.
            try { localStorage.removeItem(`vendor_cart_${store.vendorId}`); } catch { /* noop */ }
          }
        } catch (err: any) {
          // #1 : session VIVANTE sur un autre poste (409 SESSION_LOCKED) OU vendeur en train
          // d'encaisser ailleurs (PAYMENT_IN_PROGRESS) → on reflète le vendeur mais SANS lock
          // (« Ailleurs »), sans voler. L'opérateur cliquera pour reprendre. On reste online.
          const mountCode = err?.response?.data?.code;
          if (mountCode === 'SESSION_LOCKED' || mountCode === 'PAYMENT_IN_PROGRESS') {
            logger.warn('[VendorSession] Montage : vendeur actif/encaisse sur un autre poste — pas de vol');
            const vendor = await api.getVendor(store.vendorId!).catch(() => ({
              id: store.vendorId!,
              name: store.currentVendorName || 'Vendeur',
              role: store.currentVendorRole || 'CASHIER',
              active: true,
            } as Vendor));
            // Le vendeur travaille AILLEURS : le panier autoritatif est sur l'autre
            // poste. Ce que ce poste garde en mémoire persistée est un reliquat, et
            // il ne pourra jamais être sauvegardé (pas de lock). L'afficher fait
            // croire à un panier en cours. Même raison que le 409 du changement de
            // vendeur, qui supprime déjà le filet local « il écraserait une copie
            // plus fraîche ».
            // ...SAUF si ce poste porte un panier non synchronisé et récent : au
            // marché, il peut en être le SEUL exemplaire (sauvegarde refusée faute
            // de réseau). Un lock détenu ailleurs ne prouve pas que CE panier-là a
            // été traité. On ne jette donc que le reliquat sans filet.
            if (!readPendingCart(store.vendorId!)) {
              const reliquat = (usePOSStore.getState().carts[store.vendorId!] || []).length;
              if (reliquat > 0) {
                logger.log(`[VendorSession] Montage : vendeur actif ailleurs → reliquat local effacé (${reliquat} ligne(s))`);
              }
              store.clearCart();
              usePOSStore.setState((s) => ({
                tares: { ...s.tares, [store.vendorId!]: 0 },
                activeTareGrams: 0,
              }));
              try { localStorage.removeItem(`vendor_cart_${store.vendorId}`); } catch { /* noop */ }
            }

            setState((prev) => ({
              ...prev,
              currentVendor: vendor as Vendor,
              session: null,
              hasLock: false,
              isOnline: true,
              loading: false,
              error: 'Session active sur un autre poste',
            }));
            return;
          }
          if (vendeurRefuse(err)) {
            oublierVendeurInactif(store.vendorId!, store.currentVendorName);
            return;
          }
          logger.warn('[VendorSession] Failed to re-acquire on mount:', err);
          // Hors-ligne : pas de session serveur possible, mais on reflète le vendeur
          // persisté (store) pour que le POS reste pleinement utilisable — UI vendeur,
          // auto-connexion balance, et panier déjà restauré par la persistance du store.
          empreinteMontageRef.current = empreintePanier(usePOSStore.getState());
          setState((prev) => ({
            ...prev,
            currentVendor: {
              id: store.vendorId!,
              name: store.currentVendorName || 'Vendeur',
              role: store.currentVendorRole || 'CASHIER',
              active: true,
            } as Vendor,
            session: null,
            hasLock: true,
            isOnline: false,
            loading: false,
          }));
        }
      })();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Ping automatique toutes les 2 secondes ──────────────────
  // NOTE: le ping continue même en "offline" pour détecter le retour réseau.
  // On ne stoppe l'intervalle que s'il n'y a pas de session.
  useEffect(() => {
    if (!state.session) {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      return;
    }

    const sessionVendorId = state.session.vendor_id;

    const pingSession = async () => {
      // `navigator.onLine` est un INDICE, pas une preuve. Safari/macOS le laisse
      // parfois figé à `false` après un réveil Wi-Fi, et il ne repasse jamais à
      // `true` tout seul. Or ce garde empêchait le ping de partir : la caisse
      // restait « hors ligne » indéfiniment alors que le réseau fonctionnait,
      // sans rien pour la détromper — c'est un piège fermé sur lui-même, la
      // seule preuve du retour réseau étant justement la requête qu'on refuse
      // d'envoyer. Le même travers est déjà signalé plus haut pour le cas SANS
      // session (« un événement online/offline manqué… laisse isOnline figé »),
      // mais la réconciliation qui l'y rattrape ne tourne pas quand un vendeur
      // est connecté.
      //
      // On garde l'économie de requêtes (pas de ping toutes les 2 s au marché
      // sans réseau) en n'essayant qu'un cycle sur cinq, soit ~10 s.
      if (!navigator.onLine) {
        pingFailCountRef.current += 1;
        if (pingFailCountRef.current >= 3) {
          setState((prev) => prev.isOnline ? { ...prev, isOnline: false } : prev);
        }
        if (pingFailCountRef.current % 5 !== 0) return;
      }

      try {
        // Verrou paiement : on transmet l'état « paiement en cours » de CE poste au backend
        // via le heartbeat. Tant que c'est true, la session est marquée « en encaissement »
        // → une reprise distante est refusée (anti double-vente). Lu en direct (non stale).
        const paymentInProgress = getPOSStoreSnapshot().paymentInProgress;
        const response = await api.pingVendorSession(
          sessionVendorId,
          workstationId.current,
          paymentInProgress,
        );

        // Garde anti-ping périmé : si la session courante a changé pendant que CE ping
        // était en vol (switch de vendeur), son résultat concerne l'ANCIEN vendeur et ne
        // doit pas être appliqué — sinon un ping sortant qui se résout après le switch
        // écraserait hasLock du NOUVEAU vendeur (faux « Ailleurs » clignotant en plein
        // marché). sessionRef est le miroir non-stale de state.session.
        if (sessionRef.current?.vendor_id !== sessionVendorId) {
          return;
        }

        // Ping réussi → remettre online si nécessaire
        if (pingFailCountRef.current > 0) {
          logger.log('🟢 Connexion rétablie après', pingFailCountRef.current, 'échecs');
        }
        pingFailCountRef.current = 0;
        setState((prev) => !prev.isOnline ? { ...prev, isOnline: true } : prev);

        if (!response.hasLock) {
          lockLostCountRef.current += 1;
          if (lockLostCountRef.current === 1) {
            logger.warn('⚠️ Lock perdu - session prise sur un autre poste');
            setState((prev) => ({
              ...prev,
              hasLock: false,
              error: 'Session active sur un autre poste',
            }));
          }
        } else {
          if (lockLostCountRef.current > 0) {
            logger.log('✅ Lock récupéré après perte');
          }
          lockLostCountRef.current = 0;
          setState((prev) => ({ ...prev, hasLock: true, error: null }));

          // #5 : le ping est désormais un HEARTBEAT PUR (maintien du lock + TTL + détection
          // « Ailleurs »). L'auto-save panier qui était greffé ici a été RETIRÉ : il lisait
          // getCurrentVendorCart() (= store.vendorId courant) mais sauvait sous sessionVendorId
          // → pendant un switch ces deux ids divergeaient → panier d'un vendeur écrit sous
          // l'autre (le bug #2). La sauvegarde est confiée à l'unique écrivain debounced.
        }
      } catch (error) {
        pingFailCountRef.current += 1;
        logger.warn(`[VendorSession] Ping échoué (${pingFailCountRef.current}/3):`, error);

        // Déclarer offline seulement après 3 échecs consécutifs (~6s)
        if (pingFailCountRef.current >= 3) {
          setState((prev) => prev.isOnline ? { ...prev, isOnline: false } : prev);
        }
        // Le ping CONTINUE de tourner pour auto-détecter le retour réseau
      }
    };

    pingSession();
    pingIntervalRef.current = setInterval(pingSession, 2000);

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [state.session]);

  // ─── Auto-save real-time du panier ───────────────────────────
  // Sauvegarde DB debounced (250ms) + backup localStorage immédiat à chaque
  // modification du panier. Garantit zéro perte en cas de switch POS, freeze
  // navigateur, ou crash bridge. Couplé à un toast d'alerte si le save échoue
  // (lock perdu = session ailleurs).
  const cart = useVendorCart();
  const cartCustomerId = usePOSStore((s) => s.customerId);
  const cartPreorderId = usePOSStore((s) => s.preorderId);
  const cartTareGrams = usePOSStore((s) => s.activeTareGrams);
  const cartInvoiceRequested = usePOSStore((s) => s.invoiceRequested);
  const setCartLastSavedAt = usePOSStore((s) => s.setCartLastSavedAt);
  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!state.session) {
      // Pas de session : aucune sauvegarde serveur possible. Le panier n'existe
      // qu'ici — on le met dans le filet local pour qu'aucun remontage ne le
      // prenne pour un reste périmé (incident du 10/10/2026).
      const live = usePOSStore.getState();
      // Panier réhydraté au montage, pas encore touché : on ne le ré-horodate pas.
      if (empreinteMontageRef.current !== null) {
        if (empreintePanier(live) === empreinteMontageRef.current) return;
        empreinteMontageRef.current = null;
      }
      if (live.vendorId && state.hasLock && !switchInProgress.current) {
        writePendingCart(live.vendorId, {
          lines: live.carts[live.vendorId] || [],
          customerId: live.customerId,
          preorderId: live.preorderId,
          activeTareGrams: live.tares[live.vendorId] ?? live.activeTareGrams,
          invoiceRequested: live.invoiceRequested,
          ecomOrderId: live.ecomOrderId,
          ecomOrderPaid: live.ecomOrderPaid,
          ecomOrderDueCents: live.ecomOrderDueCents,
          ecomOrderPaidCents: live.ecomOrderPaidCents,
        });
      }
      return;
    }
    const vendorId = state.session.vendor_id;

    // 1. Backup localStorage IMMÉDIAT (synchrone, jamais en échec)
    try {
      localStorage.setItem(
        `vendor_cart_backup_${vendorId}`,
        JSON.stringify({
          lines: cart,
          customerId: cartCustomerId,
          preorderId: cartPreorderId,
          activeTareGrams: cartTareGrams,
          invoiceRequested: cartInvoiceRequested,
          timestamp: Date.now(),
        }),
      );
    } catch (e) {
      logger.warn('[VendorSession] Backup localStorage échoué:', e);
    }

    // 2. Save API debounced 250ms (regroupe les modifs rapides)
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    if (!state.isOnline || !state.hasLock) return;

    saveDebounceRef.current = setTimeout(async () => {
      // #2 — GARDES ANTI-COURSE (le debounced est le SEUL écrivain) :
      // (a) un switch est en cours → ne rien sauver ;
      // (b) re-valider le vendorId LIVE juste avant l'appel : s'il a changé depuis l'arme
      //     du debounce (transition de switch), abandonner — sinon on sauverait le panier
      //     d'un vendeur sous l'id d'un autre (bug #2) ;
      // (c) lire le panier via getState().carts[vendorId] (et non la closure) pour que
      //     SOURCE et CIBLE soient le MÊME id.
      if (switchInProgress.current) return;
      const live = usePOSStore.getState();
      if (live.vendorId !== vendorId) return;
      const linesToSave = live.carts[vendorId] || [];
      try {
        await api.saveVendorCart(
          vendorId,
          {
            lines: linesToSave,
            customerId: live.customerId,
            preorderId: live.preorderId,
            activeTareGrams: live.tares[vendorId] ?? live.activeTareGrams,
            invoiceRequested: live.invoiceRequested,
          },
          workstationId.current,
        );
        setCartLastSavedAt(Date.now());
        // Sauvegarde confirmée : le filet « non synchronisé » n'a plus d'objet.
        try { localStorage.removeItem(`vendor_cart_${vendorId}`); } catch { /* noop */ }
      } catch (err: any) {
        if (err?.response?.status === 409) {
          logger.warn('[VendorSession] ⛔ saveCart 409 — session sur un autre poste');
          toast.error('Panier non sauvegardé — session prise par un autre poste', { duration: 8000 });
          setState((prev) => ({ ...prev, hasLock: false, error: 'Session active sur un autre poste' }));
        } else {
          // Serveur injoignable : ce panier n'existe plus qu'ici → filet local.
          logger.warn('[VendorSession] Save real-time échoué:', err);
          writePendingCart(vendorId, {
            lines: linesToSave,
            customerId: live.customerId,
            preorderId: live.preorderId,
            activeTareGrams: live.tares[vendorId] ?? live.activeTareGrams,
            invoiceRequested: live.invoiceRequested,
            ecomOrderId: live.ecomOrderId,
            ecomOrderPaid: live.ecomOrderPaid,
            ecomOrderDueCents: live.ecomOrderDueCents,
            ecomOrderPaidCents: live.ecomOrderPaidCents,
          });
        }
      }
    }, 250);

    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
  }, [cart, cartCustomerId, cartPreorderId, cartTareGrams, cartInvoiceRequested, state.session, state.isOnline, state.hasLock, setCartLastSavedAt]);

  // ─── Switch vendeur local dégradé (pas de lock serveur) ───
  // Réseau marché instable + 4 vendeurs / 3 POS : on ne bloque plus le changement de
  // vendeur. Panier sortant conservé en local, panier entrant restauré depuis le filet
  // local, lock serveur différé (ré-acquis au retour réseau via handleOnline).
  // ⚠️ Le PIN ne peut PAS être vérifié hors-ligne — on l'accepte (vendeurs de confiance,
  // physiquement présents). La vérification reprend dès le retour en ligne.
  // Utilisé : (a) quand on est déjà offline, (b) en fallback quand un appel réseau du
  // switch online timeout (coupure réseau détectée pendant le switch).
  const performOfflineSwitch = useCallback(async (newVendorId: string): Promise<boolean> => {
    try {
      const store = getPOSStoreSnapshot();

      // Conserver le panier sortant en local (filet non synchronisé)
      if (store.vendorId && store.vendorId !== newVendorId) {
        const outgoing = store.getCurrentVendorCart();
        if (outgoing.length > 0 || store.activeTareGrams > 0 || store.customerId || store.invoiceRequested) {
          try {
            localStorage.setItem(`vendor_cart_${store.vendorId}`, JSON.stringify({
              vendorId: store.vendorId,
              lines: outgoing,
              customerId: store.customerId,
              preorderId: store.preorderId,
              activeTareGrams: store.activeTareGrams,
              invoiceRequested: store.invoiceRequested,
          ecomOrderId: store.ecomOrderId,
          ecomOrderPaid: store.ecomOrderPaid,
          ecomOrderDueCents: store.ecomOrderDueCents,
          ecomOrderPaidCents: store.ecomOrderPaidCents,
              timestamp: Date.now(),
            }));
          } catch (e) {
            logger.warn('[VendorSession] Filet local panier sortant échoué:', e);
          }
        }
      }

      // Résolution du NOM du vendeur — on NE veut JAMAIS afficher l'UUID dans l'en-tête POS
      // (bug observé : « cf26ac02-… » au lieu de « Philippe »). Ce chemin (switch offline OU
      // repli quand un switch online time-out) ne doit pas dépendre du seul cache IndexedDB
      // (peuplé par CatalogSync, parfois incomplet/non synchronisé). Cascade de replis :
      //   1. cache IndexedDB (sync catalogue) ;
      //   2. cache localStorage de la VendorQuickBar (toujours peuplé, contient les noms) ;
      //   3. nom courant si on reste sur le même vendeur, sinon « Vendeur » — jamais l'UUID.
      let vendorName = '';
      let vendorRole: VendorRole = 'CASHIER';
      try {
        const cached: any = await offlineDb.vendors.get(newVendorId);
        if (cached?.name) {
          vendorName = cached.name;
          vendorRole = (cached.role as VendorRole) || 'CASHIER';
        }
      } catch (e) {
        logger.warn('[VendorSession] Vendeur introuvable dans le cache offline:', e);
      }
      if (!vendorName) {
        try {
          const raw = localStorage.getItem('pos_vendors_cache');
          if (raw) {
            const v = (JSON.parse(raw) as any[]).find((x) => x.id === newVendorId);
            if (v?.name) {
              vendorName = v.name;
              vendorRole = (v.role as VendorRole) || vendorRole;
            }
          }
        } catch { /* noop */ }
      }
      if (!vendorName) {
        vendorName = (store.vendorId === newVendorId && store.currentVendorName) || 'Vendeur';
      }

      // Basculer localement
      store.setVendorId(newVendorId);
      store.setCurrentVendorName(vendorName);
      store.setCurrentVendorRole(vendorRole);
      store.setVendorWorkstations([]);
      if (vendorRole === 'ADMIN') store.setFullAccess(true); else store.resetFullAccess();

      // Restaurer le panier entrant depuis le filet local
      const pending = readPendingCart(newVendorId);
      if (pending) {
        store.loadCartFromSnapshot(pending);
        logger.log(`[VendorSession] 📴 Switch offline → ${vendorName} (panier local: ${pending.lines.length} lignes)`);
      } else {
        store.clearCart();
        logger.log(`[VendorSession] 📴 Switch offline → ${vendorName} (aucun panier local)`);
      }

      lockLostCountRef.current = 0;
      lastSavedCartHashRef.current = '';
      setState({
        currentVendor: { id: newVendorId, name: vendorName, role: vendorRole, active: true } as Vendor,
        session: null,
        hasLock: true,
        isOnline: false,
        loading: false,
        error: null,
      });
      return true;
    } catch (err: any) {
      logger.error('[VendorSession] Switch offline échoué:', err);
      setState((prev) => ({ ...prev, loading: false, error: 'Changement de vendeur hors-ligne échoué' }));
      return false;
    }
  }, []);

  // ─── Switch vendeur ──────────────────────────────────────────
  const switchVendor = useCallback(
    async (newVendorId: string, pin?: string, takeover: boolean = false, onLocked?: (workstation?: string) => void): Promise<boolean> => {
      // Garde paiement (symétrique du logout) : interdit de changer de vendeur pendant un
      // paiement en cours (sinon panier/écran modifiés sous un Bancontact en attente).
      if (getPOSStoreSnapshot().paymentInProgress) {
        toast.error('Paiement en cours — terminez ou annulez avant de changer de vendeur');
        return false;
      }
      // #2 : verrou anti-course posé pour TOUTE la transition (gèle les auto-save jusqu'au
      // finally), même sur les chemins de retour anticipé (offline, PIN, 409).
      switchInProgress.current = true;
      try {
      if (!state.isOnline) {
        return await performOfflineSwitch(newVendorId);
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // 1. Vérifier le PIN si fourni
        if (pin) {
          const verifyResponse = await api.verifyVendorPin(newVendorId, pin);
          if (!verifyResponse.valid) {
            setState((prev) => ({ ...prev, loading: false, error: 'Code PIN incorrect' }));
            return false;
          }
        }

        // Lecture depuis Zustand getState() — jamais stale
        const store = getPOSStoreSnapshot();

        // Re-acquire du même vendeur (clic sur "Ailleurs") ?
        const isReacquire = store.vendorId === newVendorId && !state.hasLock;

        if (!isReacquire) {
          // 2. Sauvegarder le panier du vendeur actuel (+ tare) — UNIQUEMENT si ce poste
          // DÉTIENT encore le lock du vendeur sortant. S'il est en « Ailleurs » (lock pris
          // par un autre poste, ou session expirée/relâchée → workstation '__released__'),
          // son panier autoritatif vit ailleurs (autre poste ou snapshot serveur). Tenter de
          // le sauver d'ici renvoie un 409 (workstation ≠) → popup « panier non sauvegardé »
          // à tort, ET écraserait une copie plus fraîche par notre copie périmée. On s'abstient.
          // (Avant le chantier, le force=true systématique masquait ce cas : on « détenait »
          // toujours le lock. Retirer le force l'a révélé.)
          const currentCart = store.getCurrentVendorCart();
          // Persister même si panier sans ligne mais qu'une info l'accompagne : tare active,
          // client attribué, ou demande de facture (préparation d'une facture avant de scanner).
          if (state.hasLock && store.vendorId && (currentCart.length > 0 || store.activeTareGrams > 0 || store.customerId || store.invoiceRequested)) {
            const outgoingPayload: CartPayload = {
              lines: currentCart,
              customerId: store.customerId,
              preorderId: store.preorderId,
              activeTareGrams: store.activeTareGrams,
              invoiceRequested: store.invoiceRequested,
          ecomOrderId: store.ecomOrderId,
          ecomOrderPaid: store.ecomOrderPaid,
          ecomOrderDueCents: store.ecomOrderDueCents,
          ecomOrderPaidCents: store.ecomOrderPaidCents,
            };
            const result = await saveVendorCartResilient(store.vendorId, outgoingPayload, workstationId.current);
            if (result === 'ok') {
              logger.log(`[VendorSession] Panier de ${store.currentVendorName || 'vendeur'} sauvegardé (${currentCart.length} lignes, tare: ${store.activeTareGrams}g)`);
              // Save serveur OK → on retire un éventuel filet local devenu obsolète
              try { localStorage.removeItem(`vendor_cart_${store.vendorId}`); } catch { /* noop */ }
            } else if (result === 'conflict') {
              // 409 : ce poste ne détient (plus) PAS le lock du vendeur sortant — son hasLock
              // local était périmé (le ping n'avait pas encore vu la reprise ailleurs). Le
              // panier autoritatif est sur l'autre poste / le snapshot serveur. On NE prévient
              // PAS (rien n'est perdu) et on n'écrit PAS de filet local (il écraserait une
              // copie plus fraîche au prochain retour). On enchaîne sur l'acquisition cible.
              logger.log(`[VendorSession] Save panier sortant ignoré (lock ailleurs, 409) pour ${store.currentVendorName || 'vendeur'}`);
              try { localStorage.removeItem(`vendor_cart_${store.vendorId}`); } catch { /* noop */ }
            } else {
              // 'unreachable' : serveur injoignable (réseau marché) → le panier n'est QUE local.
              // On le conserve sous la clé « non synchronisée ». handleOnline le repoussera au
              // retour réseau, et il sera restauré si le vendeur revient sur CE poste avant la
              // synchro (au lieu d'être vidé).
              try {
                localStorage.setItem(`vendor_cart_${store.vendorId}`, JSON.stringify({
                  vendorId: store.vendorId,
                  ...outgoingPayload,
                  timestamp: Date.now(),
                }));
              } catch (e) {
                logger.warn('[VendorSession] Filet local panier échoué:', e);
              }

              // Garde-fou : le panier n'a PAS atteint le serveur (réseau). On demande
              // confirmation avant de quitter le vendeur (sinon ce panier ne pourra pas
              // « suivre » sur un autre poste — il restera uniquement sur ce Mac mini). Rien
              // n'est encore libéré à ce stade : annuler laisse le vendeur en place sans perte.
              const proceed = window.confirm(
                `⚠️ Le panier de ${store.currentVendorName || 'ce vendeur'} n'a PAS pu être ` +
                `sauvegardé sur le serveur (réseau).\n\n` +
                `Si vous changez de vendeur maintenant, ce panier restera UNIQUEMENT sur ce ` +
                `poste — il ne suivra pas sur un autre Mac mini.\n\n` +
                `Changer de vendeur quand même ?`,
              );
              if (!proceed) {
                logger.log('[VendorSession] Changement de vendeur annulé par l\'opérateur (panier non sauvegardé)');
                setState((prev) => ({ ...prev, loading: false }));
                return false;
              }
              toast.warning(`Panier de ${store.currentVendorName || 'ce vendeur'} conservé en local sur ce poste uniquement`);
            }
          }

          // 3 (repoussé) : la libération du vendeur SORTANT se fait désormais APRÈS
          // l'acquisition réussie (voir 4b). Si l'acquisition échoue (paiement en cours,
          // vendeur occupé, réseau), on ne libère PAS → ce poste reste proprement sur SON
          // vendeur, sans « Session active ailleurs » parasite (reorder acquérir-puis-libérer).
        } else {
          logger.log(`[VendorSession] Re-acquire de ${store.currentVendorName || 'vendeur'} — skip save/release, récupération du panier distant`);
        }

        // 4. Acquérir nouvelle session
        // #1 : plus de force aveugle. takeover=true UNIQUEMENT si l'opérateur a confirmé
        // « reprendre ici » (propagé depuis VendorQuickBar). Sinon, un vendeur déjà actif
        // ailleurs renvoie 409 → l'UI invite à recliquer pour reprendre.
        const sessionResponse = await api.acquireVendorSession(
          newVendorId,
          workstationId.current,
          false,
          takeover,
        );

        // 4a. Stocker le token
        if (sessionResponse.access_token) {
          localStorage.setItem('auth_token', sessionResponse.access_token);
          logger.log(`[VendorSession] Token JWT stocké pour ${newVendorId}`);
        }

        // 4b. Libérer le vendeur SORTANT — seulement MAINTENANT que la nouvelle session est
        // acquise (reorder). Avant, on libérait AVANT d'acquérir : un refus d'acquisition
        // (paiement en cours, vendeur occupé, réseau) laissait ce poste sans session pour son
        // propre vendeur → « Session active ailleurs » parasite. Désormais on n'arrive ici
        // qu'en cas de succès, donc le sortant n'est libéré que si la bascule aboutit.
        if (!isReacquire && store.vendorId && store.vendorId !== newVendorId) {
          try {
            await api.releaseVendorSession(store.vendorId, workstationId.current);
          } catch (err) {
            logger.warn('Failed to release outgoing session:', err);
          }
        }

        // 5. Charger les infos du nouveau vendeur
        const vendor = await api.getVendor(newVendorId);

        // 6. Restaurer le panier sauvegardé.
        // Le snapshot serveur est la source de vérité inter-postes. Mais s'il ne renvoie
        // rien d'exploitable (offline, OU sauvegarde précédente échouée → snapshot vide),
        // on bascule sur le filet local « non synchronisé », plus récent que le serveur.
        let savedCart = sessionResponse.cart_snapshot;
        const serverHasCart = !!(
          savedCart && ((savedCart.lines?.length ?? 0) > 0 || (savedCart.activeTareGrams ?? 0) > 0
            || savedCart.customerId || savedCart.invoiceRequested)
        );
        if (!serverHasCart) {
          const pending = readPendingCart(newVendorId);
          if (pending) {
            savedCart = pending;
            // Consommé une seule fois (même boucle de résurrection qu'au montage) ;
            // l'auto-save repousse ce panier au serveur dans les 2 s.
            try { localStorage.removeItem(`vendor_cart_${newVendorId}`); } catch { /* noop */ }
            logger.log(`[VendorSession] Panier restauré depuis le filet local (${pending.lines.length} lignes) — snapshot serveur vide/indispo`);
          }
        }

        // 7. Mettre à jour le store Zustand AVANT de restaurer le panier
        store.setVendorId(newVendorId);
        store.setCurrentVendorName(vendor.name);
        store.setCurrentVendorRole(vendor.role);

        if (sessionResponse.workstation_codes) {
          store.setVendorWorkstations(sessionResponse.workstation_codes);
          logger.log(`[VendorSession] Workstations assignées: ${sessionResponse.workstation_codes.join(', ')}`);
        } else {
          store.setVendorWorkstations([]);
        }

        if (vendor.role === 'ADMIN') {
          store.setFullAccess(true);
        } else {
          store.resetFullAccess();
        }

        // 8. Restaurer le panier sauvegardé (+ tare, client, demande de facture — même sans ligne)
        const hasLines = savedCart?.lines && savedCart.lines.length > 0;
        const hasTare = (savedCart?.activeTareGrams ?? 0) > 0;
        const hasMeta = !!(savedCart?.customerId || savedCart?.invoiceRequested);
        if (savedCart && (hasLines || hasTare || hasMeta)) {
          logger.log(`[VendorSession] Restauration pour ${vendor.name} (${savedCart.lines?.length || 0} lignes, tare: ${savedCart.activeTareGrams ?? 0}g, client: ${savedCart.customerId ? 'oui' : 'non'}, facture: ${savedCart.invoiceRequested ? 'oui' : 'non'})`);
          store.loadCartFromSnapshot(savedCart);
        } else {
          logger.log(`[VendorSession] Aucun panier/tare sauvegardé pour ${vendor.name}, état vide`);
          store.clearCart();
        }

        // 9. Reset des refs pour la nouvelle session
        lockLostCountRef.current = 0;
        lastSavedCartHashRef.current = '';

        // 10. Mettre à jour l'état de la session (déclenche le re-run du ping effect)
        setState({
          currentVendor: vendor,
          session: sessionResponse,
          hasLock: true,
          isOnline: true,
          loading: false,
          error: null,
        });

        logger.log(`✅ Session acquise pour ${vendor.name}`);

        // 11. Sync catalogue en arrière-plan (ne bloque pas le login)
        // Utilise le tenant du React context pour éviter des décalages avec localStorage.
        // ⚠️ NE re-synchroniser QUE si réellement périmé (needsSync, seuil 4 h). Avant, un
        // syncAll() partait à CHAQUE switch vendeur (~645×/poste/jour en marché) → ~0,35 Go
        // de 4G gaspillés et charge serveur inutile (le catalogue ne change pas en marché).
        try {
          if (tenant?.id) {
            const catalogSync = new CatalogSync(tenant.id);
            // Garde COURT (3 min) : on saute la re-sync uniquement si le catalogue vient
            // d'être synchronisé. Ça neutralise les switches rapides (la vraie cause du
            // gaspillage) SANS périmer le catalogue (corrections de stock visibles <3 min).
            catalogSync.getLastSyncDate().then((last) => {
              const recent = last && (Date.now() - last.getTime() < 3 * 60 * 1000);
              if (recent) {
                logger.log('[VendorSession] Catalogue synchronisé il y a <3 min — sync ignorée');
                return;
              }
              catalogSync.syncAll().then((result) => {
                logger.log(`[VendorSession] Catalogue synchronisé: ${result.products.synced} produits`);
              }).catch((err) => {
                logger.warn('[VendorSession] Sync catalogue échouée (non bloquant):', err);
              });
            }).catch((err) => {
              logger.warn('[VendorSession] getLastSyncDate échoué (non bloquant):', err);
            });
          } else {
            logger.warn('[VendorSession] Sync catalogue ignorée : pas de tenant chargé');
          }
        } catch (err) {
          logger.warn('[VendorSession] Impossible de lancer la sync catalogue:', err);
        }

        // Index d'empreintes F&L (reconnaissance "qui apprend") — non bloquant.
        // ⚠️ Sync INCRÉMENTALE : l'index n'est plus « léger » (7,1 Mo au 09/08/2026,
        // il grossit à chaque encodage appris). Le télécharger en entier à chaque
        // prise de session, donc à chaque changement de vendeur, coûtait 13 Go de
        // données sur une seule journée — et autant d'allocations dans l'onglet.
        try {
          const posId = localStorage.getItem('pos_id') || undefined;
          setProduceSource(posId);
          loadFlCategorySet().catch(() => { /* repli is_weighted si échec */ });
          syncProduceIndex().then((r) => {
            logger.log(`[VendorSession] Index F&L: ${r.total} empreintes (+${r.nouveaux})`);
          }).catch(() => { /* non bloquant */ });
        } catch (err) {
          logger.warn('[VendorSession] Chargement index F&L échoué (non bloquant):', err);
        }

        return true;
      } catch (error: any) {
        logger.error('Switch vendor failed:', error);

        if (error.response?.data?.code === 'PAYMENT_IN_PROGRESS') {
          // Verrou paiement : le vendeur cible encaisse sur un autre poste. On NE propose PAS
          // de reprise (pas de onLocked) — reprendre pendant un paiement = risque de
          // double-vente. Message clair, l'opérateur réessaie une fois l'encaissement fini.
          setState((prev) => ({ ...prev, loading: false, error: 'Vendeur en train d\'encaisser sur un autre poste' }));
          toast.error('Ce vendeur est en train d\'encaisser sur un autre poste — réessayez dans un instant', { duration: 6000 });
          return false;
        }

        if (error.response?.data?.code === 'SESSION_LOCKED') {
          setState((prev) => ({ ...prev, loading: false, error: `${error.response.data.message}` }));
          // #1 (régression bloquante) : la session du vendeur cible est VIVANTE sur un autre
          // poste. On le signale à l'UI pour qu'elle propose une reprise explicite (takeover)
          // — un 409 ne doit JAMAIS laisser le vendeur sans chemin de reprise atteignable.
          onLocked?.(error.response?.data?.workstation);
          return false;
        }

        // Timeout (axios ECONNABORTED) ou serveur injoignable (pas de réponse) = coupure réseau
        // détectée PENDANT le switch online. Au lieu de figer/échouer, on bascule en switch
        // local dégradé (le ping ré-acquerra le lock au retour réseau). Évite le blocage
        // « changement de vendeur » sur coupure réseau.
        const isNetworkError = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || !error.response;
        if (isNetworkError) {
          logger.warn('[VendorSession] Switch online en échec réseau/timeout → fallback switch offline');
          return performOfflineSwitch(newVendorId);
        }

        setState((prev) => ({ ...prev, loading: false, error: error.message || 'Erreur lors du changement de vendeur' }));
        return false;
      }
      } finally {
        // #2 : toujours relâcher le verrou anti-course, succès comme échec.
        switchInProgress.current = false;
      }
    },
    [state.isOnline, state.hasLock, tenant?.id, performOfflineSwitch],
  );

  // ─── Logout ──────────────────────────────────────────────────
  const logout = useCallback(async () => {
    const store = getPOSStoreSnapshot();
    if (!store.vendorId) {
      logger.warn('[VendorSession] ⚠️ logout() ignoré car pas de vendorId');
      return;
    }

    // ⚠️ Bloquer la déconnexion si un dialog de paiement est ouvert
    // (cf. bug du 29/05/2026 : 52,15 € perdus, panier purgé pendant un Bancontact en attente)
    if (store.paymentInProgress) {
      logger.warn('[VendorSession] ⛔ Déconnexion refusée : paiement en cours');
      toast.error('Paiement en cours — terminez ou annulez avant de changer de vendeur');
      return;
    }

    const vendorName = store.currentVendorName || 'Vendeur inconnu';
    logger.log(`[VendorSession] 🚪 Déconnexion en cours de ${vendorName}...`);

    try {
      const currentCart = store.getCurrentVendorCart();
      // Persister même si panier vide mais tare active
      if (currentCart.length > 0 || store.activeTareGrams > 0) {
        if (state.isOnline) {
          await api.saveVendorCart(store.vendorId, {
            lines: currentCart,
            customerId: store.customerId,
            preorderId: store.preorderId,
            activeTareGrams: store.activeTareGrams,
          }, workstationId.current);
          logger.log(`[VendorSession] Panier de ${vendorName} sauvegardé en DB (${currentCart.length} lignes, tare: ${store.activeTareGrams}g)`);
        } else {
          const offlineCart = {
            vendorId: store.vendorId,
            lines: currentCart,
            customerId: store.customerId,
            preorderId: store.preorderId,
            activeTareGrams: store.activeTareGrams,
            timestamp: Date.now(),
          };
          localStorage.setItem(`vendor_cart_${store.vendorId}`, JSON.stringify(offlineCart));
          logger.log(`[VendorSession] Panier de ${vendorName} sauvegardé en localStorage (offline)`);
        }
      }

      if (state.isOnline) {
        await api.releaseVendorSession(store.vendorId, workstationId.current);
      }

      // Nettoyer les paniers localStorage (isolation inter-tenant)
      // ⚠️ En mode offline, on PRÉSERVE les vendor_cart_* — ils contiennent le seul backup
      // du panier en cours (cf. fallback offline ci-dessus). La purge est sûre uniquement
      // si la sauvegarde DB a réussi.
      if (state.isOnline) {
        try {
          Object.keys(localStorage)
            .filter(k => k.startsWith('vendor_cart_'))
            .forEach(k => localStorage.removeItem(k));
        } catch (e) {
          logger.warn('[VendorSession] Failed to clear localStorage carts:', e);
        }
      }

      // Nettoyer les données IndexedDB du tenant actif (produits, catégories, vendeurs, ventes en attente)
      if (tenant?.id) {
        try {
          await offlineDb.clearTenantData(tenant.id);
          logger.log(`[VendorSession] IndexedDB tenant data cleared (${tenant.id})`);
        } catch (e) {
          logger.warn('[VendorSession] Failed to clear offline DB tenant data:', e);
        }
      }

      setState({
        currentVendor: null,
        session: null,
        hasLock: false,
        isOnline: state.isOnline,
        loading: false,
        error: null,
      });

      store.setVendorId(null);
      store.setCurrentVendorName(null);
      store.setCurrentVendorRole(null);
      store.setVendorWorkstations([]);
      store.resetFullAccess();
      store.clearCart();

      logger.log(`👋 Déconnexion de ${vendorName} réussie`);
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  }, [state.isOnline, tenant?.id]);

  // ─── Save / Restore cart ─────────────────────────────────────
  const saveCart = useCallback(async () => {
    if (!state.currentVendor || !state.isOnline) return;
    try {
      const { getCurrentVendorCart, customerId, preorderId, activeTareGrams } = getPOSStoreSnapshot();
      const currentCart = getCurrentVendorCart();
      await api.saveVendorCart(state.currentVendor.id, {
        lines: currentCart,
        customerId,
        preorderId,
        activeTareGrams,
      }, workstationId.current);
      logger.log('💾 Panier sauvegardé');
    } catch (error) {
      logger.error('Save cart failed:', error);
    }
  }, [state.currentVendor, state.isOnline]);

  const restoreCart = useCallback(
    async (targetVendorId: string) => {
      if (!state.isOnline) return;
      try {
        const cartData = await api.getVendorCart(targetVendorId);
        const { clearCart: storeClearCart, loadCartFromSnapshot } = getPOSStoreSnapshot();
        if (cartData && cartData.lines && cartData.lines.length > 0) {
          loadCartFromSnapshot(cartData);
        } else {
          storeClearCart();
        }
        logger.log('📥 Panier restauré:', cartData);
      } catch (error) {
        logger.error('Restore cart failed:', error);
      }
    },
    [state.isOnline],
  );

  // ─── Connexion offline ───────────────────────────────────────
  const loginOfflineVendor = useCallback(
    (offlineVendorId: string, vendorName: string, vendorRole?: VendorRole) => {
      logger.log(`[VendorSession] 📴 Connexion offline: ${vendorName} (${offlineVendorId})`);

      const store = getPOSStoreSnapshot();
      store.setVendorId(offlineVendorId);
      store.setCurrentVendorName(vendorName);
      store.setCurrentVendorRole(vendorRole || 'CASHIER');

      const offlineCartStr = localStorage.getItem(`vendor_cart_${offlineVendorId}`);
      if (offlineCartStr) {
        try {
          const offlineCart = JSON.parse(offlineCartStr);
          if (offlineCart.lines && offlineCart.lines.length > 0) {
            store.loadCartFromSnapshot(offlineCart);
            logger.log(`[VendorSession] 📴 Panier offline restauré (${offlineCart.lines.length} lignes)`);
          }
        } catch (err) {
          logger.error('[VendorSession] Erreur parsing panier offline:', err);
        }
      }

      setState({
        currentVendor: { id: offlineVendorId, name: vendorName, role: vendorRole || 'CASHIER', active: true } as Vendor,
        session: null,
        hasLock: true,
        isOnline: false,
        loading: false,
        error: null,
      });
    },
    [],
  );

  const value: VendorSessionContextValue = {
    ...state,
    switchVendor,
    loginOfflineVendor,
    logout,
    saveCart,
    restoreCart,
  };

  return (
    <VendorSessionCtx.Provider value={value}>
      {children}
    </VendorSessionCtx.Provider>
  );
}

/**
 * Hook pour accéder à la session vendeur.
 * Doit être utilisé à l'intérieur de <VendorSessionProvider>.
 */
export function useVendorSession(): VendorSessionContextValue {
  const ctx = useContext(VendorSessionCtx);
  if (!ctx) {
    throw new Error('useVendorSession must be used within a VendorSessionProvider');
  }
  return ctx;
}
